import * as React from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "lucide-react";
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "../../lib/utils";
import { Button, buttonVariants } from "./button";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const defaultClassNames = getDefaultClassNames();

  // Ref to track if user is currently dragging to select multiple dates
  const isDraggingRef = React.useRef(false);
  // Ref to track if we're in "select mode" (true) or "deselect mode" (false)
  const dragModeRef = React.useRef<"select" | "deselect" | null>(null);

  // Add global mouseup listener to stop dragging
  React.useEffect(() => {
    const handleMouseUp = () => {
      isDraggingRef.current = false;
      dragModeRef.current = null;
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, []);

  // Check if we're in multiple mode
  const isMultipleMode = "mode" in props && props.mode === "multiple";

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-background group/calendar p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        formatWeekdayName: (date) =>
          date
            .toLocaleDateString(undefined, { weekday: "short" })
            .toUpperCase(),
        ...formatters
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-end gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-9 w-9 select-none p-0 aria-disabled:opacity-50 text-muted-foreground aria-disabled:pointer-events-none",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-9 w-9 select-none p-0 aria-disabled:opacity-50 text-muted-foreground aria-disabled:pointer-events-none",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-[--cell-size] w-full items-center justify-start px-3",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-md border",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn("absolute inset-0 opacity-0", defaultClassNames.dropdown),
        caption_label: cn(
          "select-none font-medium",
          captionLayout === "label"
            ? "text-base"
            : "[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground flex-1 select-none rounded-md text-xs font-medium tracking-wider",
          defaultClassNames.weekday
        ),
        week: cn("mt-1 grid grid-cols-7 w-full gap-1", defaultClassNames.week),
        week_number_header: cn(
          "w-[--cell-size] select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-muted-foreground select-none text-[0.8rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md",
          defaultClassNames.day
        ),
        range_start: cn(
          "bg-accent rounded-l-md",
          defaultClassNames.range_start
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("bg-accent rounded-r-md", defaultClassNames.range_end),
        today: cn(
          "bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn("text-muted-foreground", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          );
        },
        DayButton: (dayButtonProps) => (
          <CalendarDayButton
            {...dayButtonProps}
            isDraggingRef={isDraggingRef}
            dragModeRef={dragModeRef}
            isMultipleMode={isMultipleMode}
          />
        ),
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components
      }}
      {...props}
    />
  );
}

const CalendarDayButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof DayButton> & {
    isDraggingRef?: React.MutableRefObject<boolean>;
    dragModeRef?: React.MutableRefObject<"select" | "deselect" | null>;
    isMultipleMode?: boolean;
  }
>(({ className, day, modifiers, ...allProps }, forwardedRef) => {
  const defaultClassNames = getDefaultClassNames();

  // Extract custom props
  const { isDraggingRef, dragModeRef, isMultipleMode, ...props } =
    allProps as any;

  const ref = React.useRef<HTMLButtonElement>(null);
  // Track if we handled the click in mousedown to prevent double-firing
  const handledInMouseDownRef = React.useRef(false);

  // Merge refs
  React.useImperativeHandle(forwardedRef, () => ref.current!);

  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  // Store the original onClick handler
  const originalOnClick = props.onClick;

  // Handle mousedown to start drag selection
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isMultipleMode && isDraggingRef && dragModeRef && !modifiers.disabled) {
      isDraggingRef.current = true;
      // Set mode based on current selection state: if selected, we're deselecting; if not, we're selecting
      dragModeRef.current = modifiers.selected ? "deselect" : "select";
      // Also trigger selection for the first date immediately
      if (originalOnClick) {
        originalOnClick(e as any);
        handledInMouseDownRef.current = true;
      }
    }
  };

  // Handle click - calls original handler for selection
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Only call if we didn't already handle it in mousedown
    if (!handledInMouseDownRef.current && originalOnClick) {
      originalOnClick(e as any);
    }
    // Reset the flag for next interaction
    handledInMouseDownRef.current = false;
  };

  // Handle mouseenter during drag to select/deselect dates
  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (
      isMultipleMode &&
      isDraggingRef?.current &&
      dragModeRef?.current &&
      !modifiers.disabled
    ) {
      // Select or deselect based on the drag mode
      if (dragModeRef.current === "select" && !modifiers.selected) {
        // We're in select mode and this date is not selected - select it
        if (originalOnClick) {
          originalOnClick(e as any);
        }
      } else if (dragModeRef.current === "deselect" && modifiers.selected) {
        // We're in deselect mode and this date is selected - deselect it
        if (originalOnClick) {
          originalOnClick(e as any);
        }
      }
    }
  };

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "text-sm text-accent-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground focus:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:relative focus-visible:z-10 flex aspect-square h-auto w-full min-w-[--cell-size] flex-col gap-1 font-normal leading-none data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    >
      {day.date.getDate()}
    </Button>
  );
});

CalendarDayButton.displayName = "CalendarDayButton";

export { Calendar, CalendarDayButton };
